// Use environment variable for API URL, fallback to localhost for development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Helper method to get auth headers
  getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Helper method to handle API responses
  async handleResponse(response) {
    console.log('API: Handling response, status:', response.status);
    
    try {
      const data = await response.json();
      console.log('API: Response data:', data);
      
      if (!response.ok) {
        console.error('API: Response not ok:', response.status, data);
        throw new Error(data.message || 'Something went wrong');
      }
      
      return data;
    } catch (error) {
      console.error('API: Error parsing response:', error);
      throw error;
    }
  }

  // Authentication endpoints
  async register(userData) {
    console.log('API: Attempting to register user:', { ...userData, password: '[HIDDEN]' });
    console.log('API: Making request to:', `${this.baseURL}/auth/register`);
    
    try {
      const response = await fetch(`${this.baseURL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });
      
      console.log('API: Response status:', response.status);
      console.log('API: Response headers:', Object.fromEntries(response.headers.entries()));
      
      return this.handleResponse(response);
    } catch (error) {
      console.error('API: Fetch error:', error);
      throw error;
    }
  }

  async login(credentials) {
    const response = await fetch(`${this.baseURL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(credentials)
    });
    
    return this.handleResponse(response);
  }

  async getCurrentUser() {
    const response = await fetch(`${this.baseURL}/auth/me`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  async updateProfile(profileData) {
    const response = await fetch(`${this.baseURL}/auth/profile`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(profileData)
    });
    
    return this.handleResponse(response);
  }

  async updatePreferences(preferencesData) {
    const response = await fetch(`${this.baseURL}/auth/preferences`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(preferencesData)
    });
    
    return this.handleResponse(response);
  }

  async updatePassword(passwordData) {
    const response = await fetch(`${this.baseURL}/auth/password`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(passwordData)
    });
    
    return this.handleResponse(response);
  }

  async updateProfileImage(imageData) {
    console.log('API: updateProfileImage called with:', imageData);
    console.log('API: imageData type:', typeof imageData);
    console.log('API: imageData === null:', imageData === null);
    
    const requestBody = { imageData };
    console.log('API: Request body:', requestBody);
    
    const response = await fetch(`${this.baseURL}/auth/profile-image`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    console.log('API: Response status:', response.status);
    
    return this.handleResponse(response);
  }

  async forgotPassword(email) {
    const response = await fetch(`${this.baseURL}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    });
    
    return this.handleResponse(response);
  }

  async resetPassword(token, newPassword) {
    const response = await fetch(`${this.baseURL}/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ token, newPassword })
    });
    
    return this.handleResponse(response);
  }

  async verifyEmail(token) {
    const response = await fetch(`${this.baseURL}/auth/verify-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ token })
    });
    
    return this.handleResponse(response);
  }

  async resendVerificationEmail() {
    const response = await fetch(`${this.baseURL}/auth/resend-verification`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Social login endpoints - using server-side OAuth flow

  async facebookLogin(token) {
    const response = await fetch(`${this.baseURL}/auth/facebook`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ token })
    });
    
    return this.handleResponse(response);
  }

  // Debate endpoints
  async getActiveDebates() {
    const response = await fetch(`${this.baseURL}/debate/active`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  async getDebateHistory() {
    const response = await fetch(`${this.baseURL}/debate/history`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Leaderboard endpoints
  async getLeaderboard() {
    const response = await fetch(`${this.baseURL}/leaderboard`);
    return this.handleResponse(response);
  }

  async getUserStats(userId) {
    const response = await fetch(`${this.baseURL}/leaderboard/user/${userId}`);
    return this.handleResponse(response);
  }

  async updateUserStats(statsData) {
    const response = await fetch(`${this.baseURL}/auth/stats`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(statsData)
    });
    
    return this.handleResponse(response);
  }

  async completeModule(moduleData) {
    console.log('🌐 API: Calling completeModule with:', moduleData);
    const response = await fetch(`${this.baseURL}/auth/complete-module`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(moduleData)
    });
    
    console.log('🌐 API: Response status:', response.status);
    const result = await this.handleResponse(response);
    console.log('🌐 API: Response data:', result);
    return result;
  }

  async deleteAccount() {
    const response = await fetch(`${this.baseURL}/auth/delete-account`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Friends system endpoints

  // Search users
  async searchUsers(query, limit = 20) {
    const response = await fetch(`${this.baseURL}/friends/search?q=${encodeURIComponent(query)}&limit=${limit}`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Get friends list
  async getFriends() {
    const response = await fetch(`${this.baseURL}/friends/list`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Get friend requests
  async getFriendRequests() {
    const response = await fetch(`${this.baseURL}/friends/requests`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Send friend request
  async sendFriendRequest(userId) {
    console.log('🌐 API: Sending friend request to user:', userId);
    const response = await fetch(`${this.baseURL}/friends/request/send`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ userId })
    });
    
    console.log('🌐 API: Send response status:', response.status);
    const result = await this.handleResponse(response);
    console.log('🌐 API: Send response result:', result);
    return result;
  }

  // Accept friend request
  async acceptFriendRequest(userId) {
    console.log('🌐 API: Accepting friend request for user:', userId);
    const response = await fetch(`${this.baseURL}/friends/request/accept`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ userId })
    });
    
    console.log('🌐 API: Accept response status:', response.status);
    const result = await this.handleResponse(response);
    console.log('🌐 API: Accept response result:', result);
    return result;
  }

  // Decline friend request
  async declineFriendRequest(userId) {
    const response = await fetch(`${this.baseURL}/friends/request/decline`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ userId })
    });
    
    return this.handleResponse(response);
  }

  // Cancel friend request
  async cancelFriendRequest(userId) {
    const response = await fetch(`${this.baseURL}/friends/request/cancel`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ userId })
    });
    
    return this.handleResponse(response);
  }

  // Remove friend
  async removeFriend(userId) {
    const response = await fetch(`${this.baseURL}/friends/remove/${userId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Block user
  async blockUser(userId) {
    const response = await fetch(`${this.baseURL}/friends/block/${userId}`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Unblock user
  async unblockUser(userId) {
    const response = await fetch(`${this.baseURL}/friends/unblock/${userId}`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Get blocked users
  async getBlockedUsers() {
    const response = await fetch(`${this.baseURL}/friends/blocked`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Get friendship status
  async getFriendshipStatus(userId) {
    const response = await fetch(`${this.baseURL}/friends/status/${userId}`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Get user profile (for viewing other users)
  async getUserProfile(userId) {
    const response = await fetch(`${this.baseURL}/friends/profile/${userId}`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Chat system endpoints

  // Get user's conversations
  async getConversations() {
    const response = await fetch(`${this.baseURL}/chat/conversations`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Get or create chat with a user
  async getChatWithUser(userId) {
    const response = await fetch(`${this.baseURL}/chat/with/${userId}`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Get chat messages
  async getChatMessages(chatId, limit = 50, skip = 0) {
    const response = await fetch(`${this.baseURL}/chat/${chatId}/messages?limit=${limit}&skip=${skip}`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Send a message
  async sendMessage(chatId, content, type = 'text') {
    const response = await fetch(`${this.baseURL}/chat/${chatId}/messages`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ content, type })
    });
    
    return this.handleResponse(response);
  }

  // Mark messages as read
  async markMessagesAsRead(chatId) {
    const response = await fetch(`${this.baseURL}/chat/${chatId}/read`, {
      method: 'PUT',
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Delete a message
  async deleteMessage(chatId, messageId) {
    const response = await fetch(`${this.baseURL}/chat/${chatId}/messages/${messageId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Get unread message count
  async getUnreadCount() {
    const response = await fetch(`${this.baseURL}/chat/unread/count`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Get chat by ID
  async getChatById(chatId) {
    const response = await fetch(`${this.baseURL}/chat/${chatId}`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Clear chat history
  async clearChatHistory(chatId) {
    const response = await fetch(`${this.baseURL}/chat/${chatId}/clear`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Report a user
  async reportUser(userId, reportData) {
    const response = await fetch(`${this.baseURL}/users/report/${userId}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(reportData)
    });
    
    return this.handleResponse(response);
  }

  // Admin endpoints
  async getAdminDashboard() {
    const response = await fetch(`${this.baseURL}/admin/dashboard`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  async getAdminReports(page = 1, limit = 20, status = '') {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    if (status) params.append('status', status);
    
    const response = await fetch(`${this.baseURL}/admin/reports?${params}`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  async updateReportStatus(reportId, status, adminNotes = '') {
    const response = await fetch(`${this.baseURL}/admin/reports/${reportId}/status`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ status, adminNotes })
    });
    
    return this.handleResponse(response);
  }

  async getAdminUsers(page = 1, limit = 20, search = '') {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    if (search) params.append('search', search);
    
    const response = await fetch(`${this.baseURL}/admin/users?${params}`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  async getAdminUserDetails(userId) {
    const response = await fetch(`${this.baseURL}/admin/users/${userId}`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  async toggleUserBan(userId, banned, reason = '') {
    const response = await fetch(`${this.baseURL}/admin/users/${userId}/ban`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ banned, reason })
    });
    
    return this.handleResponse(response);
  }

  async getAdminStats() {
    const response = await fetch(`${this.baseURL}/admin/stats`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  async getAdminActivity(limit = 50) {
    const params = new URLSearchParams({
      limit: limit.toString()
    });
    
    const response = await fetch(`${this.baseURL}/admin/activity?${params}`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Feedback endpoints
  async submitFeedback(feedbackData) {
    const response = await fetch(`${this.baseURL}/feedback/submit`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(feedbackData)
    });
    
    return this.handleResponse(response);
  }

  async getMyFeedback(page = 1, limit = 10) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    
    const response = await fetch(`${this.baseURL}/feedback/my-feedback?${params}`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  // Admin feedback endpoints
  async getAdminFeedback(page = 1, limit = 20, status = '', category = '') {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    if (status) params.append('status', status);
    if (category) params.append('category', category);
    
    const response = await fetch(`${this.baseURL}/feedback/admin/all?${params}`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  async getAdminFeedbackStats() {
    const response = await fetch(`${this.baseURL}/feedback/admin/stats`, {
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }

  async updateFeedbackStatus(feedbackId, status, adminNotes = '') {
    const response = await fetch(`${this.baseURL}/feedback/admin/${feedbackId}/status`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ status, adminNotes })
    });
    
    return this.handleResponse(response);
  }

  async deleteFeedback(feedbackId) {
    const response = await fetch(`${this.baseURL}/feedback/admin/${feedbackId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    
    return this.handleResponse(response);
  }
}

export default new ApiService(); 